// All of CaptureCamera's state and behaviour: the page step, the camera session, the scanner,
// identity lookup, shots, the bulk session and the upload queue. The composer draws what this returns.
import { addItem, addUpload, emptySession, failLost, fileUpload, findDuplicate, loadSession, patchItem, pillState, resumeOffer, saveSession, syncStuck } from "../../lib/bulkSession";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createCameraSession, SLOW_CHECK_MS } from "../../lib/camera/cameraSession";
import { mark, span } from "../../lib/stepLog";
import { firstFrame, nextFrame } from "../../lib/camera/frames";
import { coverBand, createBarcodeScanner, decodeBarcodeFromImage, warmBarcodeDecoder } from "../../lib/liveBarcode";
import { ASKS_KEY, blockedHelp, deriveCamState, forcedCamState, nextAskCount, showAllowTip } from "../../lib/camera/cameraState";
import { identityDecision, itemLine, itemName, itemPictures, realTitle } from "../../lib/identityDecision";
import { finishSession, identify, newUploadId, removeSavedPhoto, stageShot } from "../../lib/captureClient";
import { createFrameRing, createProductRecorder } from "../../lib/trainingFrames";
import { sharedTrainingQueue } from "../../lib/trainingQueue";
import { useOverlayHistory } from "../../lib/useOverlayHistory";
import { sharedUploadQueue } from "../../lib/uploadQueue";
import { doneReturnUrl } from "../../lib/returnUrl";
import { isIOS, parseUA } from "../../lib/deviceInfo";
import { toQ, shotFlag } from "../../lib/captureQuality";
import { flushSync } from "react-dom";
import { stripTiles } from "../../lib/photoStep";
import { photoCount } from "../../lib/doneScreen";
import { analyzeCanvas, captureBestFrame, encodeFrame, previewFrame, releaseShot, scratchCanvas, thumbOf } from "./shotCapture";
/** Per product. Matches the backend's MAX_PHOTOS (scanUpload.service.js). */
export const MAX_SHOTS = 15;

/**
 * Before a product may be filed: one photograph, in every mode. The merchant
 * decides what else is worth taking (Harsh 10-28; it was two for new items).
 */
const MIN_TO_COMMIT = 1;

const MIN_TO_COMMIT_PRESET = 1;

/** What a parked product-bound capture says, by the queue's error code. */
function presetFailure(code) {
  if (code === "product_gone") return "gone";
  if (code === "signed_out") return "signed_out";
  return "failed";
}

let shotSeq = 0;

/**
 * The page phase the screen draws, from the page's own step and the camera session.
 * "locked" stays locked while the camera comes and goes (a typed code, a fallback to a tap);
 * a preset is locked from the first attached stream.
 */
function derivePhase(page, cam, preset) {
  if (page === "filing" || page === "done" || page === "gone") return page;
  if (cam.state === "denied") return "denied";
  if (cam.state === "failed") return "error";
  if (cam.state === "interrupted") return "interrupted";
  if (page === "locked" || (preset && cam.opened)) return "locked";
  const on = preset ? "locked" : "scanning";
  switch (cam.state) {
    case "idle": return cam.started ? "idle" : "resolving";
    case "requesting": return cam.resolving ? "resolving" : "starting";
    case "flipping": return on;
    case "opening":
    case "live":
    case "retuning": return cam.attached || cam.live ? on : "starting";
    default: return "resolving";
  }
}

/**
 * @param {{onBack?: () => void, preset?: {productId: string|null, barcode?: string|null, productTitle?: string|null, returnUrl?: string|null}|null}} props
 *   `preset` binds the camera to one existing product, or to one barcode that
 *   has no product yet (both from the SESSION, never the URL): no scanner, one
 *   photo is enough, and a confirmed capture ends on a Done state instead of
 *   scanning again. Absent, nothing below changes. The backend files a
 *   barcode preset under the GRANT's barcode, so nothing here sends one.
 *   `returnUrl`, when set, is already validated as this store's admin (see
 *   App.jsx and lib/returnUrl.js) and becomes the Done state's primary action.
 */
/**
 * Training must never touch a capture: every call into it goes through here.
 * `?train=0` / `?trainring=1` are read once at load and kept for the tab, so
 * they survive the redirect to the gallery.
 */
const tr = (fn) => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

const bootFlag = (name) => {
  try {
    const v = new URLSearchParams(location.search).get(name);
    if (v !== null) window.sessionStorage.setItem("cosmos.capture." + name, v);
    return window.sessionStorage.getItem("cosmos.capture." + name);
  } catch {
    return null;
  }
};

const TRAIN_FORCED_OFF = bootFlag("train") === "0";

const RING_FORCED_ON = bootFlag("trainring") === "1";

/**
 * `quick` ({returnUrl}) is a single-item Quick Scan: the bulk loop's barcode
 * step and decision, one photo minimum, then a Done state instead of the next
 * item. After the first commit pins the grant, "Take more" files bound.
 * `preset` is a session bound to one product, barcode or new item (the admin's "Capture in
 * browser"): the same photo step and saved screen as Quick scan, with no scan step.
 */
// Bulk scan's session outlives a remount but not a reload: the first read after the page loads
// finds the queue empty, so whatever was still going up is gone with it.
let bulkBooted = false;

// The welcome-back sheet is offered once per page load, by the first bulk camera to mount.
let resumeOffered = false;

function readBulk() {
  if (typeof window === "undefined") return emptySession();
  let s = loadSession(window.sessionStorage);
  if (!bulkBooted) {
    bulkBooted = true;
    s = failLost(s);
  }
  return s;
}
export function useCaptureFlow({ onBack, preset = null, quick = null, bulkReturnUrl = null, desk = false, trainingShop = null, trainingDailyMb = 0 }) {
  // Starts at `resolving`, NOT `idle`.
  //
  // This page is server-rendered, and the client cannot know whether camera
  // permission already exists until it has asked — which is asynchronous and
  // therefore after hydration. Starting at `idle` meant the "Start camera"
  // panel was in the served HTML and painted for a beat on every arrival,
  // before being replaced by the viewfinder. A button that appears and vanishes
  // reads as a glitch, and on a slow phone it is long enough to tap.
  //
  // `resolving` renders no panel at all — just the black stage the camera is
  // about to fill — so the only transition anyone sees is black to viewfinder.
  // The cold first visit still gets the idle screen, a few milliseconds later,
  // because Safari ties the permission prompt to a user gesture and a page that
  // demands the camera on load is the page a merchant denies by reflex.
  // The page's own step. The camera's phases (resolving, starting, idle, scanning, denied,
  // error, interrupted) are read off the camera session; see derivePhase.
  const [page, setPage] = useState("cam"); // cam|locked|filing|done|gone
  const sessionRef = useRef(null);
  if (!sessionRef.current) {
    sessionRef.current = createCameraSession({
      mediaDevices: typeof navigator === "undefined" ? undefined : navigator.mediaDevices,
      clock: { now: () => performance.now(), setTimeout: (f, ms) => setTimeout(f, ms), clearTimeout: (id) => clearTimeout(id) },
      storage: (() => { try { return localStorage; } catch { return undefined; } })(),
      mark,
      span,
      doc: {
        hasFocus: () => document.hasFocus?.(),
        get visibilityState() { return document.visibilityState; },
        activeTag: () => document.activeElement?.tagName,
        addEventListener: (ev, fn) => document.addEventListener(ev, fn),
        removeEventListener: (ev, fn) => document.removeEventListener(ev, fn),
      },
      video: {
        attach: (stream) => { if (videoRef.current) videoRef.current.srcObject = stream; },
        play: () => videoRef.current.play(),
        firstFrame: (ms) => firstFrame(videoRef.current, ms),
        nextFrame: (ms) => nextFrame(videoRef.current, ms),
        readyState: () => videoRef.current?.readyState ?? 0,
        paused: () => videoRef.current?.paused ?? true,
        hasSource: () => Boolean(videoRef.current?.srcObject),
        holdFrame: () => takeSnap(),
        el: () => videoRef.current,
        ready: () => Boolean(videoRef.current),
      },
      permissions: typeof navigator === "undefined" ? undefined : navigator.permissions,
      brave: typeof navigator === "undefined" ? undefined : navigator.brave,
      warm: warmBarcodeDecoder,
      releaseOnHide: !isIOS(),
    });
  }
  const session = sessionRef.current;
  const cam = useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);
  const phase = derivePhase(page, cam, Boolean(preset));
  const flipping = cam.state === "flipping";
  const [barcode, setBarcode] = useState(null);
  // Who the locked barcode belongs to, asked of the server on lock:
  // {status: checking|decide|done, barcode, candidates, target, code, title, noBarcode}.
  // `barcode` above is what was read; `ident.barcode` is what gets sent (null
  // for No barcode). A failed lookup settles to barcode-only, as before.
  const [ident, setIdent] = useState(null);
  const identRef = useRef(null);
  const identWait = useRef(null);
  const identSeq = useRef(0);
  const tilesRef = useRef(null);
  const [deciding, setDeciding] = useState(false);
  const [typing, setTyping] = useState(false);
  // Numeric keypad by default; ABC flips to the full keyboard (shelf codes can carry letters).
  const [typeAbc, setTypeAbc] = useState(false);
  // The on-screen keyboard overlays the page rather than resizing it, so the
  // typed-barcode field would sit under it. Lift the bottom block by however
  // much of the layout the keyboard is covering.
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    const stage = stageRef.current;
    if (!typing || !vv || !stage) return undefined;
    // The fade keeps reaching the bottom edge (it grows rather than moving), and
    // the reticle re-centres in what is left above the field.
    const fit = () => {
      const covered = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      stage.style.setProperty("--kb", `${Math.round(covered)}px`);
      if (covered > 0) {
        const top = 84; // the title bar
        const room = window.innerHeight - covered - 96 - top; // 96: the field and its gap
        const rw = Math.max(160, Math.min(window.innerWidth * 0.8, 360, (room - 24) / 0.6));
        stage.style.setProperty("--rw", `${Math.round(rw)}px`);
        stage.style.setProperty("--ry", `${Math.round(top + room / 2)}px`);
      } else {
        stage.style.removeProperty("--rw");
        stage.style.removeProperty("--ry");
      }
    };
    fit();
    vv.addEventListener("resize", fit);
    vv.addEventListener("scroll", fit);
    return () => {
      vv.removeEventListener("resize", fit);
      vv.removeEventListener("scroll", fit);
      stage.style.setProperty("--kb", "0px");
      stage.style.removeProperty("--rw");
      stage.style.removeProperty("--ry");
    };
  }, [typing]);
  // Scanning-screen camera controls. Torch support is read off the live track;
  // Flip shows only when the device lists a second camera.
  const [torchOn, setTorchOn] = useState(false);
  const [reading, setReading] = useState(false);
  const torchBusyRef = useRef(false);
  const typeInRef = useRef(null);
  const photoInRef = useRef(null);
  const [typed, setTyped] = useState("");
  const [shots, setShots] = useState([]);
  // `saving` now only guards a double-tap on Confirm between the hand-off and
  // the deck clearing — a few milliseconds, not a few seconds. There is no
  // progress state any more: nothing on this screen waits for an upload, and
  // the queue reports its own.
  const [saving] = useState(false);
  const [saveError, setSaveError] = useState("");
  // One-shot: drives the lock wash and the shutter pulse, then clears itself so
  // the animations do not replay on every unrelated re-render.
  const [flash, setFlash] = useState(false);
  // The white shutter blink, and the "filed" confirmation after an upload.
  const [snap, setSnap] = useState(false);
  const [filedToast, setFiledToast] = useState(null);
  // BULK SCAN's session (the pill and the list). Bulk is the unbound loop: not quick, not a
  // preset, not guided. `bulk` is the state; bulkRef is what the queue's handlers read.
  const isBulk = !quick && !preset;
  const isBulkRef = useRef(isBulk);
  isBulkRef.current = isBulk;
  const [bulk, setBulkState] = useState(() => (isBulk ? readBulk() : emptySession()));
  const bulkRef = useRef(bulk);
  const bulkEdit = useCallback((fn) => {
    const next = fn(bulkRef.current);
    bulkRef.current = next;
    setBulkState(next);
    if (typeof window !== "undefined") saveSession(window.sessionStorage, next);
  }, []);
  // The green moment after a save, the connection, and which sheet is open:
  // null | "list" | an item's key.
  const [bulkSaved, setBulkSaved] = useState(null);
  const [online, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine !== false);
  const [blView, setBlView] = useState(null);
  const [blConfirm, setBlConfirm] = useState(false);
  const [finishing, setFinishing] = useState(false);
  // The in-between windows: a barcode already in this scan ({item, n}; dupGoRef carries on with
  // the lookup's normal flow), the × with photos taken ("bulk" | "quick"), and the welcome back.
  const [dup, setDup] = useState(null);
  const dupGoRef = useRef(null);
  const [discardAsk, setDiscardAsk] = useState(null);
  const [resume, setResume] = useState(false);
  // The item "Add more photos" was opened for: the next hand-off files onto it.
  const addToRef = useRef(null);
  // What the background uploader is doing. Summary only — the queue owns the
  // bytes and this page never reaches into it.
  const [queue, setQueue] = useState({ pending: 0, uploading: false, failed: 0, current: null });
  // The shot being examined full-screen, if any. Checking a photograph at the
  // shelf costs seconds; discovering it was unreadable at enrichment costs a
  // second trip to the same aisle.
  const [inspecting, setInspecting] = useState(null);
  const queueRef = useRef(null);
  const handlersRef = useRef(null);
  // PRESET ONLY: the uploadId of the capture we are waiting on. Done is shown
  // when the queue reports THAT id filed — never on hand-off, which only
  // proves the bytes were accepted into memory.
  const [awaiting, setAwaiting] = useState(null);
  const awaitingRef = useRef(null);
  // The queue's handlers are built once, so they read the preset through this.
  const presetRef = useRef(preset);
  presetRef.current = preset;
  // Set when Done navigated back on its own, so the panel says so rather than
  // offering a button for the moment the Shopify app takes over.
  const [autoReturn, setAutoReturn] = useState(false);
  // Quick scan's saved screen: how many photos went, and the first shot kept
  // alive for the picture of a new item.
  const [doneSnap, setDoneSnap] = useState(null);
  // What the screen calls the thing being photographed. A barcode preset has
  // no product yet, so it is named by the barcode the merchant just scanned —
  // the one thing on screen they can check against the pack in their hand.
  const doneSnapRef = useRef(null);
  // Step-log clocks (ms, performance.now): scanning began, barcode locked, sheet shown, Done tapped.
  const scanT0 = useRef(0);
  const [allowTipOff, setAllowTipOff] = useState(() => { try { return localStorage.getItem("cap-allow-tip-off") === "1"; } catch { return false; } });
  // Only the hidden ?debug=camstate=resuming|flip needs a snapshot of its own; the session holds the real ones.
  const [forcedSnap, setForcedSnap] = useState(null);
  const snapMode = cam.snap || forcedSnap;
  const [snapMirror, setSnapMirror] = useState(false);
  const snapRef = useRef(null);
  const beginScanningRef = useRef(null); // resolveIdentity is built before beginScanning (they call each other)
  const flipTry = useRef(0); // 1: a flip is being tried, 2: it fell back to the previous camera
  const forcedCam = useMemo(() => forcedCamState(), []);
  const lockT0 = useRef(0);
  const sheetT0 = useRef(0);
  const doneT0 = useRef(0);
  const finishEnd = useRef(null);
  // Bulk opened from the desktop QR: Finish tells the desktop, then shows an end screen.
  const [deskEnd, setDeskEnd] = useState(null); // { n } once finished
  const deskRef = useRef(desk);
  deskRef.current = desk;
  doneSnapRef.current = doneSnap;
  const quickRef = useRef(quick);
  const bulkReturnRef = useRef(bulkReturnUrl);
  bulkReturnRef.current = bulkReturnUrl;
  quickRef.current = quick;
  const quickMode = Boolean(quick);
  // Quick and bound sessions share the photo step and the saved screen.
  const savedUi = quickMode || Boolean(preset);
  // Set once a quick scan's first commit has landed (the server pinned the grant).
  const quickPinnedRef = useRef(false);
  const presetTitle = !preset
    ? quickMode
      ? ident?.title || ident?.barcode || ident?.code || "this item"
      : ""
    : preset.candidate
    ? itemName(preset.candidate)
    : preset.newItem
    ? preset.barcode || preset.productCode || "No barcode"
    : preset.barcode
    ? `barcode ${preset.barcode}`
    : "this product";
  // The callbacks below depend on WHETHER there is a preset, never on the
  // object: App rebuilds it every render, and depending on its identity
  // re-created start/resume/confirm (and re-ran the auto-start effect) each time.
  const isPreset = Boolean(preset);
  const minToCommit = isPreset || quickMode ? MIN_TO_COMMIT_PRESET : MIN_TO_COMMIT;

  const videoRef = useRef(null);
  const backdropRef = useRef(null);
  const reticleRef = useRef(null);
  const stageRef = useRef(null);
  const topRef = useRef(null);
  const footRef = useRef(null);
  const scannerRef = useRef(null);
  // Shots live in a ref as well as state purely so unmount can revoke their
  // object URLs. Reading state in a cleanup closure gives you the array as it
  // was when the effect ran, which on this page is always the empty one.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const shotsRef = useRef([]);
  shotsRef.current = shots;
  // The deck's upload id and each staged shot's slot. A finished shot is PUT at
  // once under this id, so Done only commits. The id is spent by the hand-off.
  const deckRef = useRef({ id: null, slots: new Map() });
  const stageDeckShot = useCallback((shotId, blob) => {
    const deck = deckRef.current;
    deck.id = deck.id || newUploadId();
    const live = new Set(shotsRef.current.map((s) => s.id));
    const used = new Set([...deck.slots].filter(([k]) => live.has(k) && k !== shotId).map(([, v]) => v));
    let slot = 0;
    while (used.has(slot)) slot += 1;
    deck.slots.set(shotId, slot);
    stageShot(blob, deck.id, slot);
  }, []);
  /** The id to file the deck under: the one its shots were staged with. */
  const deckUploadId = () => (deckRef.current.id = deckRef.current.id || newUploadId());
  const spendDeckId = () => {
    deckRef.current = { id: null, slots: new Map() };
  };
  // Training frames (off unless the session says so; ?train=0 forces it off).
  // The ring samples while locked; the recorder keeps one product's frames.
  const trainOn = Boolean(trainingShop) && !TRAIN_FORCED_OFF;
  const ringRef = useRef(null);
  const recRef = useRef(null);
  const getRing = () => {
    ringRef.current = ringRef.current || createFrameRing({ force: RING_FORCED_ON });
    return ringRef.current;
  };
  const dropTraining = useCallback(() => {
    tr(() => recRef.current?.discard());
    recRef.current = null;
  }, []);
  const trainingRec = useCallback(() => {
    if (!recRef.current) {
      recRef.current = createProductRecorder({
        shop: trainingShop,
        ring: getRing(),
        getVideoSize: () => {
          const v = videoRef.current;
          return v?.videoWidth ? { w: v.videoWidth, h: v.videoHeight } : null;
        },
      });
    }
    return recRef.current;
  }, [trainingShop]);
  // Build the product's pack and stage it. The sender releases it only once
  // THIS upload has committed (queue-level listener), and photos always go first.
  const handOffTraining = useCallback((uploadId, deck) => {
    const rec = recRef.current;
    recRef.current = null;
    tr(() =>
      rec
        ?.finalize({ uploadId, photoIndexById: new Map(deck.map((s, i) => [s.id, i])) })
        .then((pack) => pack && sharedTrainingQueue().stage(pack))
        .catch(() => {}),
    );
  }, []);
  // A different shop's session must not inherit another's staged packs.
  useEffect(() => {
    if (!trainOn) return;
    tr(() => sharedTrainingQueue().setShop(trainingShop));
    tr(() => sharedTrainingQueue().setBudget((Number(trainingDailyMb) || 0) * 1024 * 1024));
  }, [trainOn, trainingShop, trainingDailyMb]);
  // takeShot fires from a stable callback and must read the CURRENT phase, not
  // the one captured when it was created.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  /**
   * Keep the viewfinder honest: inset the video clear of the chrome, and draw
   * the capture area exactly where the browser is painting it.
   *
   * ⚠️ MEASURED AT RUNTIME, NOT ASSUMED. Three things move independently — the
   * header grows a second row when a 13-digit barcode will not fit beside the
   * buttons, the uploader line appears and disappears, and the frame's own
   * aspect is whatever the handset granted rather than whatever was asked for.
   * Any hard-coded inset is wrong on the first device that disagrees, and being
   * wrong here means showing a merchant a boundary their photographs do not
   * actually have.
   *
   * The painted rect has to be computed because object-fit:contain letterboxes
   * INSIDE the element: the black bars are part of the box, so no CSS selector
   * addresses the picture. Same arithmetic the browser does — fit by whichever
   * axis runs out first.
   */
  useEffect(() => {
    const stage = stageRef.current;
    const video = videoRef.current;
    if (!stage || !video) return undefined;

    let raf = 0;
    const measure = () => {
      raf = 0;
      const top = topRef.current?.offsetHeight || 0;
      const foot = footRef.current?.offsetHeight || 0;
      stage.style.setProperty("--chrome-top", `${top}px`);
      stage.style.setProperty("--chrome-bottom", `${foot}px`);

      const bw = video.clientWidth;
      const bh = video.clientHeight;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      // No frame yet: hide the outline rather than draw a box around nothing.
      if (!bw || !bh || !vw || !vh) {
        stage.style.setProperty("--fw", "0px");
        stage.style.setProperty("--fh", "0px");
        return;
      }
      // The frame's own aspect, published so the tile strip can be the shape of
      // the photograph rather than a square that hides its top and bottom. It
      // is written HERE, from the same videoWidth/videoHeight the capture area
      // is computed from, so the two cannot drift apart.
      stage.style.setProperty("--shot-ar", String(vw / vh));

      const scale = Math.min(bw / vw, bh / vh);
      const w = vw * scale;
      const h = vh * scale;
      stage.style.setProperty("--fx", `${(bw - w) / 2}px`);
      // Relative to the STAGE, and the video is already pushed down by the
      // header — so the header's height is part of this offset.
      stage.style.setProperty("--fy", `${top + (bh - h) / 2}px`);
      stage.style.setProperty("--fw", `${w}px`);
      stage.style.setProperty("--fh", `${h}px`);
    };

    // Coalesced: a resize observer on three elements plus orientation change
    // can fire a dozen times in a frame, and this writes layout-affecting
    // properties.
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(stage);
    ro.observe(video);
    if (topRef.current) ro.observe(topRef.current);
    if (footRef.current) ro.observe(footRef.current);

    video.addEventListener("loadedmetadata", schedule);
    video.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    schedule();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      video.removeEventListener("loadedmetadata", schedule);
      video.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
    };
    // ⚠️ `phase`, NOT `live`. `live` is derived from phase and declared far
    // below this effect, so naming it here is a temporal dead zone: the module
    // builds, the tests pass, and every render throws "Cannot access 'live'
    // before initialization" — a blank page. Caught by loading the page, which
    // is the only thing that would have caught it.
    //
    // The rest of the list is everything that changes the chrome's height: the
    // header grows a row when a barcode locks, the tile strip appears with the
    // first shot, and the uploader line comes and goes.
  }, [phase, shots.length, queue.pending, queue.failed]);

  // Keep the newest shot in view; only when a shot is added or removed.
  useEffect(() => {
    const el = tilesRef.current;
    if (!el || shots.length === 0) return;
    const calm = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ left: el.scrollWidth, behavior: calm ? "auto" : "smooth" });
  }, [shots.length]);

  // The back gesture closes the inspector and leaves the merchant ON THE
  // PRODUCT they were photographing. Without it, back popped App's entry and
  // threw them out of the camera entirely — losing the deck and the lock for a
  // gesture that meant "stop looking at this photo". Same hook the gallery
  // viewer uses; see useOverlayHistory for why a button close has to unwind
  // its own history entry.
  const closeInspect = useCallback(() => setInspecting(null), []);
  useOverlayHistory(Boolean(inspecting), closeInspect);
  // Escape closes the inspector. Owned here because ZoomableImage own
  // Escape handling comes bundled with its close button, and this screen wants
  // the key without the second cross.
  useEffect(() => {
    if (!inspecting) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setInspecting(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inspecting]);

  // ONE queue for the life of the component. Rebuilding it on a re-render would
  // strand the captures it is holding, which is the exact failure the whole
  // design of uploadQueue.js exists to prevent.
  // THE SHARED QUEUE, not one of this component's own.
  //
  // It used to be created here and stopped in the unmount cleanup below, which
  // meant navigating away destroyed the pending captures and the photographs
  // they held. See sharedUploadQueue's header for what that cost.
  if (!queueRef.current) queueRef.current = sharedUploadQueue();
  if (!handlersRef.current) {
    handlersRef.current = ({
      onChange: setQueue,
      onFiled: ({ barcode: filed, code, label, count: localCount, stored, result, uploadId }) => {
        // The server's number once the commit has returned; the local one only if it did not say.
        const count = photoCount(localCount, stored);
        if (Number.isFinite(stored) && stored !== localCount) mark("filed_mismatch", { local: localCount, stored });
        // PRESET: our capture is committed. Clear the deck and say Done; the
        // barcode toast would name an empty string.
        if (awaitingRef.current) {
          if (uploadId !== awaitingRef.current) return;
          awaitingRef.current = null;
          setAwaiting(null);
          if (quickRef.current || presetRef.current) {
            const [first, ...rest] = shotsRef.current;
            rest.forEach(releaseShot);
            setDoneSnap({ n: shotsRef.current.length, stored, first: first || null });
          } else {
            shotsRef.current.forEach(releaseShot);
          }
          setShots([]);
          setPage("done");
          try {
            navigator.vibrate?.([25, 40, 25]);
          } catch {
            /* best-effort */
          }
          // STRAIGHT BACK, but only while the Done tap still counts as a tap.
          // Android hands an admin.shopify.com link to the Shopify app only for
          // a navigation with a user gesture behind it; one without stays in
          // the browser and lands on the web admin, possibly signed out. The
          // commit is async, so the gesture is Chrome's transient activation
          // (about 5s after the tap). Inside it, go; outside it, or where the
          // API is missing, the Done panel's button is the way back.
          quickPinnedRef.current = quickRef.current != null;
          const back = doneReturnUrl(presetRef.current, quickRef.current);
          mark("filed", { ms: performance.now() - doneT0.current });
          // iOS: a scripted navigation lands on the web admin (Universal Links need a tap) and
          // the shopify:// scheme never opened the app (0 of 5 in the phone lab, 10-30) while
          // Safari asked to "open another application". The Done screen's Back to Shopify tap is
          // the way back there.
          const ios = isIOS();
          if (back && ios) {
            mark("return_to_shopify", { mode: "tap", reason: "ios" });
          } else if (back) {
            const act = navigator.userActivation?.isActive;
            mark("return_to_shopify", { mode: "auto", fired: Boolean(act), activation: navigator.userActivation ? act : "n/a" });
            if (act) {
              setAutoReturn(true);
              window.location.assign(back);
            }
          }
          return;
        }
        // BULK: the pill says it, not the toast.
        if (isBulkRef.current) {
          const cur = bulkRef.current;
          const at = cur.items.findIndex((it) => it.uploads.some((u) => u.id === uploadId));
          if (at >= 0) {
            const it = cur.items[at];
            const itemCode = result?.variantCode || result?.code || null;
            const name = it.title ? it.name : itemCode || it.name;
            bulkEdit((s) => fileUpload(s, uploadId, { name, code: itemCode || it.code || null, productId: result?.productId || it.productId, savedAt: Date.now() }, stored));
            setBulkSaved({ n: at + 1, name });
          }
          try {
            navigator.vibrate?.([25, 40, 25]);
          } catch {
            /* best-effort */
          }
          return;
        }
        // The confirmation still names the product, and now it lands when the
        // product is actually FILED rather than when it was handed over. The
        // merchant is usually one or two items further along by then, which is
        // the point: it says "that one is safe", not "please wait".
        setFiledToast({ barcode: label || filed || code || "No barcode", count });
        try {
          navigator.vibrate?.([25, 40, 25]);
        } catch {
          /* best-effort */
        }
      },
      onExpired: () => setSaveError("Signed out. Your photos are still here — ask your admin for a new link."),
    });
  }

  // The beforeunload guard used to live here. It is registered by
  // sharedUploadQueue now, because it is the QUEUE's promise and the queue
  // outlives this screen — a guard that unmounted with the camera stopped
  // warning the moment someone tapped back. See uploadQueue.js.


  // CLAIM THE HANDLERS IN AN EFFECT, not during render.
  //
  // Doing it in render meant this screen claimed them before the screen it was
  // replacing had unmounted, and that screen's cleanup then cleared them — the
  // uploader row went silent for the whole of the next upload. Effects run
  // after the outgoing cleanup, and release() checks ownership anyway, so the
  // two now compose in either order.
  useEffect(() => {
    const q = queueRef.current;
    const release = q.setHandlers(handlersRef.current);
    setQueue(q.summary());
    return release;
  }, []);

  // ⚠️ READ THE QUEUE ON MOUNT, do not wait to be told.
  //
  // `queue` starts at zeroes, and the shared queue only calls onChange when
  // something MOVES. So coming back to the camera while an upload was already
  // running left the row hidden for the whole of that upload — the person had
  // no way to know anything was in flight. It was correct when the queue was
  // created fresh per mount and empty by definition; it stopped being correct
  // the moment the queue outlived the screen.
  // ⚠️ UNMOUNTING NO LONGER STOPS THE QUEUE, and that is the fix rather than an
  // oversight.
  //
  // This used to `stop()` on cleanup. The queue was owned by this component, so
  // navigating away both halted the pump AND discarded the pending items with
  // their photographs still in them — a capture that said "uploading" and then
  // never existed. Nine of Krystal's uploads reached S3 and never got a row
  // that way on 2026-09-11.
  //
  // The queue is shared and outlives this screen now, so cleanup only stops it
  // calling into a component that is gone. `resume()` stays because StrictMode
  // still runs mount -> cleanup -> mount, and a queue left stopped by a
  // previous instance must come back.
  useEffect(() => {
    queueRef.current?.resume();
    return undefined;
  }, []);

  // Release the camera and every object URL when the page goes away. A stream
  // left running keeps the recording indicator lit and the sensor warm.
  useEffect(() => {
    return () => {
      shotsRef.current.forEach(releaseShot);
      tr(() => ringRef.current?.stop());
      dropTraining();
    };
  }, [dropTraining]);

  // Sample preview frames only while locked on a product.
  useEffect(() => {
    if (!trainOn || phase !== "locked") {
      tr(() => ringRef.current?.stop());
      return undefined;
    }
    tr(() => getRing().start(videoRef.current));
    return () => tr(() => ringRef.current?.stop());
  }, [trainOn, phase]);

  // Retire the lock animation once it has played. Slightly longer than the
  // 520ms wash so the element is not pulled out from under its own fade.
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(false), 1400);
    return () => clearTimeout(t);
  }, [flash]);

  useEffect(() => {
    if (!snap) return undefined;
    const t = setTimeout(() => setSnap(false), 260);
    return () => clearTimeout(t);
  }, [snap]);

  // Long enough to read a barcode back and be sure it was the right product,
  // short enough not to sit over the next scan.
  useEffect(() => {
    if (!filedToast) return undefined;
    const t = setTimeout(() => setFiledToast(null), 2600);
    return () => clearTimeout(t);
  }, [filedToast]);

  // Bulk session upkeep: the green moment lasts 2 s, the pill follows the connection, and a
  // parked upload turns its item red (or amber when the connection is what parked it).
  useEffect(() => {
    if (!bulkSaved) return undefined;
    const t = setTimeout(() => setBulkSaved(null), 2000);
    return () => clearTimeout(t);
  }, [bulkSaved]);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  useEffect(() => {
    if (isBulk) bulkEdit((s) => syncStuck(s, queue.stuck));
  }, [isBulk, queue.stuck, bulkEdit]);

  const setIdentity = useCallback((next) => {
    identRef.current = next;
    setIdent(next);
  }, []);

  /** Ask who owns a barcode (or a typed code) and settle the target. */
  const resolveIdentity = useCallback(
    (query, scanned) => {
      const seq = ++identSeq.current;
      const back = (msg) => {
        setSaveError(msg);
        setIdentity(null);
        setBarcode(null);
        setPage("cam");
        if (session.getSnapshot().attached) beginScanningRef.current().catch(() => {});
      };
      setSaveError("");
      setIdentity({ status: "checking", barcode: scanned, candidates: [], target: null, code: null });
      const endLookup = span("lookup");
      identWait.current = identify(query)
        .then((res) => {
          if (seq !== identSeq.current) return;
          const cands = res.candidates;
          const kind = identityDecision({ candidates: cands, noBarcode: res.noBarcode, byCode: !!query.code });
          const go = () => {
          if (kind === "decide") {
            setIdentity({ status: "decide", barcode: scanned, candidates: cands, target: null, code: null, sel: cands.length === 1 ? 0 : null });
            setDeciding(true);
          } else if (res.noBarcode) {
            setIdentity({ status: "done", barcode: null, candidates: [], target: { newItem: true }, code: null, noBarcode: true });
          } else if (cands.length >= 1) {
            const c = cands[0];
            // Found by its code: what was typed is not a barcode to file.
            if (query.code) setBarcode(c.barcode || null);
            setIdentity({ status: "done", barcode: scanned || c.barcode || null, candidates: cands, target: { productId: c.productId }, code: c.code || null, title: realTitle(c) });
          } else if (query.code && query.orBarcode) {
            // Neither a known barcode nor a code: a new item with this barcode.
            setBarcode(query.orBarcode);
            setIdentity({ status: "done", barcode: query.orBarcode, candidates: [], target: null, code: null });
          } else if (query.code) {
            back("No item with that code.");
          } else if (query.orCode) {
            // Typed digits that no barcode matched may be one of our codes.
            resolveIdentity({ code: query.orCode, orBarcode: query.barcode }, null);
          } else {
            setIdentity({ status: "done", barcode: scanned, candidates: [], target: null, code: null });
          }
          };
          // Bulk: a barcode already filed in this scan asks first, then carries on as usual.
          const hit = isBulkRef.current ? findDuplicate(bulkRef.current, { barcode: scanned || query.barcode || query.orBarcode || null, candidates: cands }) : null;
          endLookup({ n: cands.length, result: hit ? "duplicate" : cands.length === 0 ? "none" : cands.length === 1 ? "one" : "several" });
          if (hit) {
            dupGoRef.current = () => seq === identSeq.current && go();
            setDup(hit);
          } else go();
        })
        .catch(() => {
          endLookup({ result: "error" });
          if (seq !== identSeq.current) return;
          if (query.code && query.orBarcode) {
            setIdentity({ status: "done", barcode: query.orBarcode, candidates: [], target: null, code: null });
          } else if (query.code) back("Could not look that code up.");
          else setIdentity({ status: "done", barcode: scanned, candidates: [], target: null, code: null });
        });
    },
    [setIdentity, session],
  );

  /** Typed text: digits are a barcode, anything else is an item code. */
  const submitTyped = useCallback(() => {
    const v = typed.trim();
    if (!v) return;
    const digits = /^\d+$/.test(v);
    setTyping(false);
    setTyped("");
    scannerRef.current?.stop();
    lockT0.current = performance.now();
    mark("barcode_lock", { ms: lockT0.current - scanT0.current, source: "typed" });
    setBarcode(v);
    setPage("locked");
    // Tried as a barcode first, then as a code. Shelf codes can carry letters
    // (RPSCANB01), and codes can be all digits.
    const orCode = digits ? (v.length === 8 ? v : undefined) : v;
    resolveIdentity({ barcode: v, orCode }, v);
  }, [typed, resolveIdentity]);

  const noBarcode = useCallback(() => {
    setTyping(false);
    setTyped("");
    scannerRef.current?.stop();
    lockT0.current = performance.now();
    mark("barcode_lock", { ms: lockT0.current - scanT0.current, source: "no_barcode" });
    identSeq.current += 1;
    setBarcode(null);
    setIdentity({ status: "done", barcode: null, candidates: [], target: { newItem: true }, code: null, noBarcode: true });
    setPage("locked");
  }, [setIdentity]);

  /** Scanning back button: Shopify in quick, the gallery in bulk. */
  const cancelTyping = useCallback(() => {
    setTyping(false);
    setTyped("");
  }, []);
  const leaveScan = useCallback(() => {
    const back = quickRef.current?.returnUrl;
    if (back) {
      window.location.assign(back);
      return;
    }
    if (
      shotsRef.current.length > 0 &&
      !window.confirm(
        "Leave without filing? " +
          shotsRef.current.length +
          (shotsRef.current.length === 1 ? " photo" : " photos") +
          " will be lost.",
      )
    ) {
      return;
    }
    bulkEdit(() => emptySession());
    // Opened from Cosmos: back to the Shopify page it started on.
    if (bulkReturnRef.current) {
      window.location.assign(bulkReturnRef.current);
      return;
    }
    if (onBack) {
      onBack();
    } else {
      window.close();
      if (window.history.length > 1) window.history.back();
    }
  }, [onBack, bulkEdit]);

  // Finish waits for what is still going up, then leaves the way Back does.
  useEffect(() => {
    if (!finishing) return;
    if (queue.failed > 0) {
      finishEnd.current?.({ ok: false, failed: queue.failed });
      finishEnd.current = null;
      setFinishing(false);
    } else if (queue.pending === 0) {
      finishEnd.current?.({ ok: true });
      finishEnd.current = null;
      setFinishing(false);
      setBlView(null);
      if (deskRef.current) {
        const n = bulkRef.current.items.length;
        bulkEdit(() => emptySession());
        setDeskEnd({ n });
        finishSession().catch((err) => console.warn("[capture] finish not recorded:", err?.message));
      } else {
        leaveScan();
      }
    }
  }, [finishing, queue.pending, queue.failed, leaveScan, bulkEdit]);

  // Coming back to a bulk scan that is still open: offered once per page load.
  useEffect(() => {
    if (!isBulk) return;
    if (resumeOffer(bulkRef.current, resumeOffered)) setResume(true);
    resumeOffered = true;
  }, [isBulk]);

  const toggleTorch = useCallback(async () => {
    const track = session.getStream()?.getVideoTracks?.()[0];
    if (!track) return;
    if (torchBusyRef.current || session.getSnapshot().busy) return;
    torchBusyRef.current = true;
    const next = !torchOn;
    setTorchOn(next); // optimistic; the request takes a beat on a phone
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
    } catch {
      // The device refused. Revert, unless a flip has since replaced the stream.
      if (session.getStream()?.getVideoTracks?.()[0] === track) setTorchOn(!next);
    } finally {
      torchBusyRef.current = false;
    }
  }, [torchOn, session]);

  const choose = useCallback(
    (cand) => {
      const cur = identRef.current;
      setIdentity({
        ...cur,
        status: "done",
        target: cand ? { productId: cand.productId } : { newItem: true },
        code: cand ? cand.code || null : null,
        title: cand ? realTitle(cand) : null,
      });
    },
    [setIdentity],
  );

  const onBarcode = useCallback((value, meta) => {
    if (!value) return;
    lockT0.current = performance.now();
    mark("barcode_lock", { ms: lockT0.current - scanT0.current, frames: meta?.attempts, format: meta?.format || "", source: meta?.source || "live", barcode: value });
    // What scanning cost on this phone: looks, their average and worst cost, and
    // the preview frame rate the merchant actually saw while aiming.
    const sp = scannerRef.current?.stats;
    const v = videoRef.current;
    if (sp?.ticks && sp.runMs > 0) {
      mark("scan_perf", {
        engine: scannerRef.current.engine || "", ticks: sp.ticks, avgMs: Math.round(sp.tickMs / sp.ticks), maxMs: sp.maxTickMs,
        fps: Math.round((sp.frames * 10000) / sp.runMs) / 10, cam: `${v?.videoWidth}x${v?.videoHeight}`,
        cores: navigator.hardwareConcurrency || 0, memGb: navigator.deviceMemory || 0,
      });
    }
    setTyping(false);
    setTyped("");
    setBarcode(value);
    resolveIdentity({ barcode: value }, value);
    setPage("locked");
    setFlash(true);
    // A double tap, not a single buzz. One short pulse is indistinguishable
    // from the phone's own notifications; a deliberate pattern reads as "the
    // thing you were doing just succeeded". Absent on iOS; harmless there.
    try {
      navigator.vibrate?.([35, 45, 70]);
    } catch {
      /* vibrate is best-effort */
    }
  }, [resolveIdentity]);

  const beginScanning = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    if (!scannerRef.current) {
      // While the preview fills the screen, read where the reticle actually is.
      const getBand = () => {
        const r = reticleRef.current;
        if (!r || !stageRef.current?.classList.contains("cap-stage--scan")) return null;
        return coverBand(r.getBoundingClientRect(), video.getBoundingClientRect(), video.videoWidth, video.videoHeight);
      };
      scannerRef.current = createBarcodeScanner({ video, onResult: onBarcode, getBand });
    }
    scanT0.current = performance.now();
    await scannerRef.current.start();
    mark("scanner_ready", { ms: performance.now() - scanT0.current, engine: scannerRef.current.engine || "none" });
    if (!session.getSnapshot().lowRes) setTimeout(() => session.reportSlow(() => scannerRef.current?.stats), SLOW_CHECK_MS);
  }, [onBarcode, session]);
  beginScanningRef.current = beginScanning;

  /** The current frame onto the snapshot canvas, for the blurred hold over a gap. */
  const takeSnap = useCallback(() => {
    const v = videoRef.current;
    const c = snapRef.current;
    if (!v || !c || !v.videoWidth) return false;
    try {
      const k = Math.min(1, 640 / v.videoWidth);
      c.width = Math.round(v.videoWidth * k);
      c.height = Math.round(v.videoHeight * k);
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      setSnapMirror(session.getSnapshot().facing === "user");
      return true;
    } catch {
      return false;
    }
  }, [session]);

  /** A chosen photo is read like a live scan; no code leaves a line and stays. */
  const readPhoto = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setSaveError("");
      setReading(true);
      const code = await decodeBarcodeFromImage(file);
      setReading(false);
      if (!["scanning", "resolving", "starting", "idle"].includes(phaseRef.current)) return;
      if (code) {
        scannerRef.current?.stop();
        onBarcode(code, { source: "upload" });
      } else {
        setSaveError("No barcode found in that photo. Try a sharper one, or type it.");
      }
    },
    [onBarcode],
  );

  // The camera lives in the session. The page tells it where it is (the step, and the quality
  // that step wants), starts it on arrival and lets go on unmount.
  const phaseStep = phase === "locked" ? "locked" : ["scanning", "starting", "idle", "resolving"].includes(phase) ? "scanning" : "other";
  useEffect(() => {
    session.setStep(phaseStep);
  }, [session, phaseStep]);
  useEffect(() => {
    void session.setQuality(phase === "locked" ? "photo" : "scan");
  }, [session, phase]);
  useEffect(() => {
    session.start({ auto: true });
    return () => session.stop();
  }, [session]);

  // The scanner follows the stream: a new one for each attached stream while the page is on the
  // scan step, gone with it. A scanner that will not start on a flipped camera sends the flip back.
  useEffect(() => {
    if (!cam.attached) return undefined;
    let cancelled = false;
    if (phaseRef.current === "scanning") {
      beginScanning().then(
        () => { flipTry.current = 0; },
        (err) => {
          if (cancelled) return;
          if (flipTry.current === 1) {
            flipTry.current = 2;
            void session.flip();
          } else if (flipTry.current === 2) {
            flipTry.current = 0;
            session.markInterrupted();
          } else {
            session.fail(err);
          }
        },
      );
    }
    return () => {
      cancelled = true;
      scannerRef.current?.dispose();
      scannerRef.current = null;
    };
  }, [cam.streamId, cam.attached]);

  // A new stream starts with the torch off.
  useEffect(() => {
    setTorchOn(false);
  }, [cam.streamId]);

  useEffect(() => {
    session.markBrave();
  }, [session]);

  const startCamera = useCallback(() => void session.start(), [session]);
  const resumeCamera = useCallback(() => void session.resume(), [session]);
  const tapCamera = useCallback(() => void session.tap(), [session]);
  const flipCamera = useCallback(() => {
    if (phaseRef.current === "scanning") flipTry.current = 1;
    void session.flip();
  }, [session]);

  /**
   * One photograph — answered INSTANTLY, finished in the background.
   *
   * ---------------------------------------------------------------------
   * THE GAP IS REAL AND IT CANNOT BE REMOVED
   * ---------------------------------------------------------------------
   * The capture samples five preview frames, keeps the sharpest and encodes
   * it once at up to 3072px. That is a few hundred milliseconds to a second,
   * and it is the price of a photograph a model can read fine print off: the
   * label close-up is exactly the shot where cutting resolution to feel fast
   * would defeat the point of taking it.
   *
   * What was wrong was not the delay. It was that the UI did nothing during
   * it: the shutter went dead, no tile appeared, and the merchant had no way
   * to tell a slow capture from a missed tap. So the two are SPLIT:
   *
   *   NOW    a frame grabbed off the running preview - one drawImage at
   *          thumbnail size, a millisecond or two - becomes the tile
   *          immediately, with the flash and the haptic.
   *   LATER  the real capture replaces that tile's image in place, with a
   *          thumbnail of the frame that was actually filed.
   *
   * The merchant sees a photograph appear the instant they press, and never
   * waits on the part that is slow. The tile carries a `pending` mark so the
   * difference is honest rather than hidden, and Confirm refuses to upload
   * while anything is still pending — the thumbnail is a placeholder, not the
   * image we file.
   *
   * The shutter also no longer blocks: each capture samples at its own tap
   * and only the encode is queued behind the previous one, so a merchant can
   * fire front, back and panel in quick succession.
   */
  const takeShot = useCallback(async () => {
    if (phaseRef.current !== "locked") return;
    // shotsRef, not the `shots` closure: rapid taps all read the same stale
    // array otherwise and sail straight past the cap.
    if (shotsRef.current.length >= MAX_SHOTS) return;

    const video = videoRef.current;
    // A presence check only. The size is NOT carried down from here any more —
    // both the thumbnail and the capture re-read it at the moment they draw, so
    // a stream that renegotiates between this line and the shutter's last
    // sample cannot end up squeezed into a stale rect. See captureBestFrame.
    if (!video?.videoWidth || !video.videoHeight) return;

    shotSeq += 1;
    const id = "s" + shotSeq;
    const shotIndex = shotsRef.current.length;
    const endShot = span("shot");
    const rec = trainOn ? tr(() => trainingRec()) : null;
    tr(() => rec?.addShot(id));

    // --- NOW -------------------------------------------------------------
    // Flash and haptic fire on the tap itself; the tile follows once its placeholder is drawn.
    setSnap(true);
    try {
      navigator.vibrate?.(15);
    } catch {
      /* best-effort */
    }
    // A shutter pressed while the camera is still being raised to photo size
    // waits for it (rarely: it runs while the barcode is looked up).
    await Promise.race([session.tuned(), new Promise((r) => setTimeout(r, 1500))]);
    const previewUrl = await previewFrame(video);
    setShots((prev) =>
      prev.length >= MAX_SHOTS
        ? prev
        : prev.concat({
            id,
            previewUrl,
            url: null,
            blob: null,
            bytes: 0,
            pending: true,
          }),
    );

    // --- LATER -----------------------------------------------------------
    // What the training recorder is told when this shot ends, whichever way.
    let train = { drop: true };
    try {
      const isWanted = () => mountedRef.current && shotsRef.current.some((s) => s.id === id);
      const picked = await captureBestFrame(video, isWanted);
      if (!picked) {
        if (!isWanted()) {
          URL.revokeObjectURL(previewUrl);
          train = { thumb: null };
          return;
        }
        throw new Error("no_frame");
      }

      const blob = picked.blob;
      // Removed, reset or unmounted while capturing: no URL to create or leak.
      if (!isWanted()) {
        URL.revokeObjectURL(previewUrl);
        train = { thumb: picked.thumb };
        return;
      }
      const flag = shotFlag(picked.metrics, { shotIndex });
      const finalUrl = URL.createObjectURL(blob);
      const filedPreview = picked.thumb ? URL.createObjectURL(picked.thumb) : previewUrl;

      // The URLs are released together when the shot is removed. The tap-time
      // placeholder is revoked below, outside the updater, which StrictMode
      // would run twice.
      setShots((prev) =>
        prev.map((s) =>
          s.id === id
            ? {
                ...s,
                url: finalUrl,
                previewUrl: filedPreview,
                blob,
                bytes: blob.size,
                pending: false,
                score: picked.score,
                q: toQ(picked.metrics, { h: "" }),
                flag,
              }
            : s,
        ),
      );
      if (filedPreview !== previewUrl) URL.revokeObjectURL(previewUrl);
      endShot({ idx: shotIndex, bytes: blob.size, ok: true });
      stageDeckShot(id, blob);
      train = { thumb: picked.thumb, q: toQ(picked.metrics, { h: "" }) };
    } catch {
      endShot({ idx: shotIndex, ok: false });
      setShots((prev) => prev.filter((s) => s.id !== id));
      URL.revokeObjectURL(previewUrl);
      setSaveError("That photo did not capture. Take it again.");
    } finally {
      tr(() => (train.drop ? rec?.dropShot(id) : rec?.finishShot(id, train.thumb, train.q)));
    }
  }, [trainOn, trainingRec, stageDeckShot]);

  const removeShot = useCallback((id) => {
    // The revoke reads from the ref and happens OUTSIDE the updater. It used to
    // sit inside it, which StrictMode runs twice — the second pass revoking a
    // URL the first had already released. See the gotcha note in the vault.
    const hit = shotsRef.current.find((s) => s.id === id);
    setShots((prev) => prev.filter((s) => s.id !== id));
    if (hit) releaseShot(hit);
    tr(() => recRef.current?.markDeleted(id));
  }, []);

  /** Upload: each chosen file goes through the shutter's analyse and encode, then lands as a shot. */
  const addFiles = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      if (phaseRef.current !== "locked" || shotsRef.current.length >= MAX_SHOTS) break;
      let bmp = null;
      try {
        bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
        let metrics = null;
        try {
          metrics = analyzeCanvas(bmp, bmp.width, bmp.height, scratchCanvas);
        } catch {
          /* unscorable: the shot carries no quality record */
        }
        const out = await encodeFrame({ src: bmp, width: bmp.width, height: bmp.height }, () => mountedRef.current);
        if (!out || !mountedRef.current) continue;
        shotSeq += 1;
        const url = URL.createObjectURL(out.blob);
        const previewUrl = out.thumb ? URL.createObjectURL(out.thumb) : url;
        const shot = {
          id: "s" + shotSeq,
          step: null,
          previewUrl,
          url,
          blob: out.blob,
          bytes: out.blob.size,
          pending: false,
          ...(metrics ? { score: metrics.sharp, q: toQ(metrics, { h: "" }), flag: shotFlag(metrics, { shotIndex: shotsRef.current.length }) } : {}),
        };
        if (shotsRef.current.length >= MAX_SHOTS) {
          releaseShot(shot);
          break;
        }
        // Set now so the next file in this batch sees the count.
        shotsRef.current = shotsRef.current.concat(shot);
        setShots((prev) => (prev.some((x) => x.id === shot.id) ? prev : prev.concat(shot)));
        stageDeckShot(shot.id, shot.blob);
      } catch {
        setSaveError("That file could not be read as a photo.");
      } finally {
        bmp?.close?.();
      }
    }
  }, [stageDeckShot]);

  /** Clear the deck and go back to reading barcodes. */
  const resetToScanning = useCallback(async () => {
    shotsRef.current.forEach(releaseShot);
    dropTraining();
    setShots([]);
    addToRef.current = null;
    setBarcode(null);
    identSeq.current += 1;
    setIdentity(null);
    setDeciding(false);
    setPage("cam");
    await beginScanning();
  }, [beginScanning, dropTraining, setIdentity]);

  /**
   * Confirm: hand the capture to the uploader and go straight back to scanning.
   *
   * ⚠️ THE DECK IS CLEARED BEFORE THE UPLOAD FINISHES, which the blocking
   * version deliberately did not do. Its reasoning still stands — "clearing
   * optimistically would lose a merchant's photographs on any failed upload,
   * and the failure this page is most likely to hit is a bad shop wifi
   * connection, which is exactly when they cannot be re-taken" — so the queue
   * carries it instead: the blobs move there and are held until the commit
   * succeeds, failures retry and then park visibly, and nothing is ever
   * discarded without the merchant saying so. See uploadQueue.js.
   *
   * What the merchant gets back is the seconds they used to spend standing in
   * an aisle watching a progress bar, several hundred times an afternoon.
   */
  // Bulk: record the hand-off in the session. An "Add more photos" capture joins its item as
  // one more upload; anything else is a new item. Pictures follow once the thumbnails exist.
  const bulkHandOff = useCallback(
    (uploadId, id, deck) => {
      const n = deck.length;
      const blobs = deck.map((s) => s.blob);
      let key = addToRef.current;
      addToRef.current = null;
      if (key && bulkRef.current.items.some((it) => it.key === key)) {
        bulkEdit((s) => addUpload(s, key, { id: uploadId, n }));
      } else {
        const cand = id.target?.productId ? (id.candidates || []).find((c) => c.productId === id.target.productId) : null;
        const images = cand ? itemPictures(cand).slice(0, 12).map(({ url, kind }) => ({ url, kind })) : [];
        key = uploadId;
        bulkEdit((s) =>
          addItem(
            s,
            {
              name: cand ? itemName(cand) : id.barcode || id.code || "No barcode",
              title: cand ? realTitle(cand) : null,
              code: cand?.code || null,
              barcode: id.barcode || null,
              kind: id.target?.productId ? "catalog" : id.noBarcode ? "nobarcode" : "new",
              productId: id.target?.productId || null,
              images,
              pictureUrl: images[0]?.url || null,
            },
            { id: uploadId, n },
          ),
        );
      }
      Promise.all(blobs.map((b) => thumbOf(b).catch(() => null))).then((th) => {
        const got = th.filter(Boolean);
        bulkEdit((s) => {
          const it = s.items.find((i) => i.key === key);
          return it ? patchItem(s, key, { thumbs: it.thumbs.concat(got), pictureUrl: it.pictureUrl || got[0] || null }) : s;
        });
      });
    },
    [bulkEdit],
  );

  /** The item page's "Add more photos": back to the photo step, aimed at that item. */
  const addMore = useCallback(
    (item) => {
      const n = shotsRef.current.length;
      if (n && !window.confirm(`Discard the ${n} photo${n === 1 ? "" : "s"} in progress?`)) return;
      shotsRef.current.forEach(releaseShot);
      dropTraining();
      setShots([]);
      identSeq.current += 1;
      addToRef.current = item.key;
      scannerRef.current?.stop();
      setTyping(false);
      setBarcode(item.barcode || null);
      setIdentity({
        status: "done",
        barcode: item.barcode || null,
        candidates: [{ productId: item.productId, title: item.title, code: item.code, images: item.images }],
        target: { productId: item.productId },
        code: item.code || null,
        title: item.title || null,
      });
      setDeciding(false);
      setBlView(null);
      setPage("locked");
    },
    [dropTraining, setIdentity],
  );

  const confirmCapture = useCallback(async () => {
    doneT0.current = performance.now();
    mark(isBulk ? "next_tap" : "done_tap", { shots: shots.length, mode: isPreset ? "preset" : quickMode ? "quick" : isBulk ? "bulk" : "plain" });
    if (isPreset || quickPinnedRef.current) {
      if (shots.length < MIN_TO_COMMIT_PRESET || saving || awaitingRef.current) return;
      // Minted here so onFiled can recognise this capture; the queue keeps it
      // fixed across retries exactly as if it had minted it.
      const id = deckUploadId();
      awaitingRef.current = id;
      const accepted = queueRef.current.add({
        barcode: "",
        shots,
        productBound: true,
        uploadId: id,
      });
      if (!accepted) {
        awaitingRef.current = null;
        setSaveError(
          "Earlier captures are still uploading. Waiting for the connection — your photos are safe.",
        );
        return;
      }
      setSaveError("");
      spendDeckId();
      handOffTraining(id, shots);
      // The deck stays until the commit lands: on failure the tiles are still
      // here, and the queue still holds the same blobs to retry.
      setAwaiting(id);
      setPage("filing");
      return;
    }

    if (!ident || shots.length < minToCommit || saving) return;

    // Identify may still be in flight; the target must be settled before filing.
    if (identRef.current?.status === "checking") await identWait.current;
    const id = identRef.current;
    if (!id) return;
    if (id.status === "decide") {
      setDeciding(true);
      return;
    }

    // The one case where handing off is refused: the queue is full, which means
    // uploads are failing or the connection has gone. Letting it grow without
    // bound would hold a merchant's whole morning in a tab's memory, and the
    // honest answer is to stop taking more until some of it lands.
    const uploadId = deckUploadId();
    // Quick scan: recognised by onFiled, which shows Done instead of the toast.
    if (quickMode) awaitingRef.current = uploadId;
    const accepted = queueRef.current.add({
      barcode: id.barcode,
      target: id.target,
      label: id.title || id.barcode || id.code || "No barcode",
      shots,
      uploadId,
    });
    if (!accepted) {
      awaitingRef.current = null;
      setSaveError(
        "Earlier captures are still uploading. Waiting for the connection — your photos are safe.",
      );
      return;
    }

    setSaveError("");
    spendDeckId();
    handOffTraining(uploadId, shots);
    if (isBulk) bulkHandOff(uploadId, id, shots);
    if (quickMode) {
      // Never back to scanning: the deck stays until the commit lands.
      setAwaiting(uploadId);
      setPage("filing");
      return;
    }
    // resetToScanning revokes the tiles' object URLs, so the queue must already
    // hold its own reference to the blobs by now — it does, `add` above.
    await resetToScanning();
  }, [ident, shots, saving, resetToScanning, isPreset, quickMode, minToCommit, handOffTraining, isBulk, bulkHandOff]);

  /** Back to the locked camera for the same product. Never the scanner. */
  const takeMore = useCallback(() => {
    shotsRef.current.forEach(releaseShot);
    releaseShot(doneSnapRef.current?.first);
    setDoneSnap(null);
    dropTraining();
    setShots([]);
    setSaveError("");
    setAutoReturn(false);
    setPage("locked");
  }, [dropTraining]);

  /** Discard: the barcode was wrong, or the photographs were. */
  const discard = useCallback(async () => {
    // Bulk and Quick scan ask with a sheet; the other modes keep the browser's confirm.
    if (shots.length > 0 && (isBulkRef.current || quickRef.current || presetRef.current)) {
      setDiscardAsk(isBulkRef.current ? "bulk" : "quick");
      return;
    }
    if (isPreset || quickPinnedRef.current) {
      // Nothing taken: × is the way out. A bound session has no back button,
      // and resetting an empty deck looked like a dead button.
      if (shots.length === 0) {
        const back = presetRef.current?.returnUrl || quickRef.current?.returnUrl;
        if (back) {
          window.location.assign(back);
        } else if (onBack) {
          onBack();
        } else {
          window.close();
          if (window.history.length > 1) window.history.back();
        }
        return;
      }
      if (
        shots.length > 0 &&
        !window.confirm(
          "Discard " + shots.length + " photo" + (shots.length === 1 ? "" : "s") + "?",
        )
      ) {
        return;
      }
      takeMore();
      return;
    }
    if (
      shots.length > 0 &&
      !window.confirm(
        "Discard " + shots.length + " photo" + (shots.length === 1 ? "" : "s") + " and scan again?",
      )
    ) {
      return;
    }
    await resetToScanning();
  }, [shots.length, resetToScanning, isPreset, takeMore, onBack]);

  useEffect(() => {
    if (!shots.length) setDiscardAsk(null);
  }, [shots.length]);

  /** The sheet's Discard: what the confirm's OK did. */
  const discardGo = useCallback(async () => {
    setDiscardAsk(null);
    if (isPreset || quickPinnedRef.current) takeMore();
    else await resetToScanning();
  }, [resetToScanning, isPreset, takeMore]);

  /** Quick scan's "Leave Quick scan": straight back to Shopify, or the gallery. */
  const leaveQuick = useCallback(() => {
    const back = quickRef.current?.returnUrl;
    if (back) window.location.assign(back);
    else if (onBack) onBack();
    else {
      window.close();
      if (window.history.length > 1) window.history.back();
    }
  }, [onBack]);

  // PRESET: the parked head is OUR capture, so the filing panel says why.
  const presetStuck =
    (preset || quick) && phase === "filing" && queue.stuck && queue.stuck.uploadId === awaiting
      ? presetFailure(queue.stuck.error)
      : null;

  const live = phase === "scanning" || phase === "locked";
  // iOS opens the keyboard only for focus() inside the tap itself, so the field
  // is rendered and focused synchronously in the Type it handler (openTyping).
  const openTyping = useCallback(() => {
    flushSync(() => {
      setTypeAbc(false);
      setTyping(true);
    });
    typeInRef.current?.focus({ preventScroll: true });
  }, []);
  const flipTypeMode = useCallback(() => {
    flushSync(() => setTypeAbc((v) => !v));
    const el = typeInRef.current;
    if (!el) return;
    // The keyboard only changes layout on a fresh focus.
    el.blur();
    el.focus({ preventScroll: true });
    const n = el.value.length;
    try { el.setSelectionRange(n, n); } catch { /* numeric inputs may refuse */ }
  }, []);
  // The redesigned barcode step: bulk and quick only, before a code is read.
  // The barcode page is the camera's start screen too: resolving, starting and
  // the needs-a-tap state all draw it, so nothing swaps while the camera opens.
  const preScan = (isBulk || quickMode) && (phase === "resolving" || phase === "starting" || phase === "idle");
  const scanStep = (isBulk || quickMode) && !isPreset;
  // No picture to be had: blocked, or no camera. They stay on the scan screen.
  const camGone = !scanStep ? null
    : forcedCam === "blocked" || forcedCam === "none" ? forcedCam
    : phase === "denied" ? "blocked" : phase === "error" && cam.noCam ? "none" : null;
  const scanUi = (phase === "scanning" || preScan || Boolean(camGone)) && !isPreset;
  const forcedWait = scanUi && ["opening", "needs_tap", "permission"].includes(forcedCam);
  const camLiveUi = cam.live && !forcedWait && !camGone;
  const camState = !scanUi ? null
    : forcedWait || forcedCam === "resuming" ? forcedCam
    : deriveCamState({ gone: camGone, snap: snapMode, camLive: camLiveUi, flipping, needsTap: cam.needsTap || cam.tapToPlay, gumWait: cam.pending > 0, perm: cam.perm });
  // Visits in a row that had to ask for the camera, counted once per visit when the permission is known.
  const [camAsks, setCamAsks] = useState(0);
  const askCounted = useRef(false);
  useEffect(() => {
    if (askCounted.current || !cam.perm) return;
    askCounted.current = true;
    let prev = 0;
    try { prev = localStorage.getItem(ASKS_KEY); } catch { /* best effort */ }
    const n = nextAskCount(prev, cam.asked);
    setCamAsks(n);
    try { localStorage.setItem(ASKS_KEY, String(n)); } catch { /* best effort */ }
  }, [cam.perm, cam.asked]);
  const allowTip = scanUi && !typing && !camState && cam.live && showAllowTip({ ...parseUA(navigator.userAgent), asked: cam.asked, dismissed: allowTipOff, asks: camAsks });
  useEffect(() => {
    if (allowTip) mark("camera_allow_tip", { shown: true, asks: camAsks });
  }, [allowTip]);
  const hideAllowTip = () => {
    mark("camera_allow_tip", { shown: false });
    setAllowTipOff(true);
    try { localStorage.setItem("cap-allow-tip-off", "1"); } catch { /* best effort */ }
  };
  const camHelp = camState === "blocked" ? blockedHelp({ ...parseUA(navigator.userAgent), brave: Boolean(navigator.brave) }) : "";
  useEffect(() => {
    if (camState) mark("camera_state", { state: camState, forced: forcedCam ? 1 : 0 });
  }, [camState, forcedCam]);
  // Forced resuming / flip need a held frame to show.
  useEffect(() => {
    if (forcedCam !== "resuming" && forcedCam !== "flip") return undefined;
    const id = setInterval(() => {
      if (takeSnap()) {
        setForcedSnap(forcedCam === "flip" ? "flip" : "resume");
        clearInterval(id);
      }
    }, 400);
    return () => clearInterval(id);
  }, [forcedCam, takeSnap]);
  const iconBtn = "cap-sbtn";
  // The redesigned photo step: the same two modes, after the barcode step.
  // Between a read and the lookup answering: the scan page stays, with one line.
  const checking = phase === "locked" && !isPreset && ident?.status === "checking";
  // A bound session (a product, a barcode or a new item from the admin) starts here: locked from
  // the first frame, with the bound item as the item on file.
  const photoUi = phase === "locked" && !checking;

  // The blurred backdrop shares the live stream only while the photo step shows.
  useEffect(() => {
    const bg = backdropRef.current;
    if (!bg) return;
    const want = photoUi && !deciding ? session.getStream() : null;
    if (bg.srcObject !== want) bg.srcObject = want;
    if (want) bg.play?.().catch(() => {});
    // inspecting: Safari may pause it while the viewer hides it; play it again on the way out.
  }, [photoUi, deciding, cam.facing, cam.streamId, flipping, session, inspecting]);
  // Step log: the choice sheet opening, and the photo step coming up.
  useEffect(() => {
    if (deciding && ident?.status === "decide") {
      sheetT0.current = performance.now();
      mark("sheet_shown", { n: ident.candidates.length });
    }
  }, [deciding, ident?.status]);
  const sheetChose = (choice) => mark("sheet_choice", { ms: performance.now() - sheetT0.current, choice });
  const photoShown = photoUi;
  useEffect(() => {
    if (photoShown) mark("photo_step_shown", { ms: lockT0.current ? performance.now() - lockT0.current : 0 });
  }, [photoShown]);
  const onFileCand = isPreset
    ? preset.candidate || null
    : ident?.status === "done" && ident.target?.productId
      ? (ident.candidates || []).find((c) => c.productId === ident.target.productId) || null
      : null;
  const isNewItem = !onFileCand;
  // Saved photos the merchant removed here; the candidate itself does not change.
  const [removedIds, setRemovedIds] = useState(() => new Set());
  const removingRef = useRef(new Set());
  const allPictures = onFileCand ? itemPictures(onFileCand) : [];
  const keptPictures = allPictures.filter((i) => !(i.id && removedIds.has(i.id)));
  // Only THIS item's removals come off its count; a fresh lookup already leaves them out.
  const removedHere = allPictures.length - keptPictures.length;
  const shownCand =
    onFileCand && removedHere
      ? {
          ...onFileCand,
          images: Array.isArray(onFileCand.images) ? keptPictures : onFileCand.images,
          photoCount: typeof onFileCand.photoCount === "number" ? Math.max(0, onFileCand.photoCount - removedHere) : onFileCand.photoCount,
        }
      : onFileCand;
  const photoTiles = photoUi ? stripTiles({ isNewItem, onFile: keptPictures, shots }) : [];
  const removeSaved = useCallback(
    async (image) => {
      const productId = onFileCand?.productId;
      // Resolves true once the server has it gone (the viewer says "Photo removed" then), false if not.
      if (!productId || !image?.id || removingRef.current.has(image.id)) return false;
      removingRef.current.add(image.id);
      const t0 = performance.now();
      setRemovedIds((prev) => new Set(prev).add(image.id));
      try {
        await removeSavedPhoto({ productId, imageId: image.id });
        mark("saved_photo_remove", { ok: 1, ms: performance.now() - t0 });
        return true;
      } catch (err) {
        // 404: already gone (a retry after a lost answer). That is what was asked for.
        if (err?.status === 404) {
          mark("saved_photo_remove", { ok: 1, status: 404, ms: performance.now() - t0 });
          return true;
        }
        mark("saved_photo_remove", { ok: 0, status: err?.status || 0, ms: performance.now() - t0 });
        setRemovedIds((prev) => {
          const next = new Set(prev);
          next.delete(image.id);
          return next;
        });
        setSaveError("That photo could not be removed. Try again.");
        return false;
      } finally {
        removingRef.current.delete(image.id);
      }
    },
    [onFileCand?.productId],
  );
  // Offline in bulk, this item's shots wait for the connection (the queue's own park counts too).
  const offlineBulk = isBulk && (!online || Boolean(queue.stuck?.network));
  const photoTitle = onFileCand ? itemName(onFileCand) : "New item";
  const photoSub = onFileCand
    ? itemLine(shownCand)
    : isPreset
      ? preset.barcode || preset.productCode || "No barcode"
      : ident?.noBarcode
        ? "No barcode"
        : ident?.barcode || barcode || "No barcode";
  const fileShotsRef = useRef(null);
  // The bulk pill: the latest item's picture and a state from the session, the connection and the queue.
  const bulkPill = isBulk ? pillState(bulk, { online, queueOffline: Boolean(queue.stuck?.network), saved: bulkSaved }) : { tone: "none" };
  const bulkLast = bulk.items[bulk.items.length - 1] || null;
  const closeBulk = () => {
    setBlView(null);
    setBlConfirm(false);
  };

  // A pending tile is a placeholder whose real image has not arrived, and its
  // `blob` is still null. Uploading then would file a product with fewer
  // photographs than the merchant can see on screen — so Confirm waits, rather
  // than silently dropping the shot that was still developing.
  const pendingShots = shots.some((s) => s.pending);
  const canConfirm =
    phase === "locked" &&
    shots.length >= minToCommit &&
    !pendingShots &&
    !saving;
    return {
    addFiles,
    addMore,
    allowTip,
    camAsks,
    autoReturn,
    awaitingRef,
    backdropRef,
    barcode,
    blConfirm,
    blView,
    bulk,
    bulkEdit,
    bulkLast,
    bulkPill,
    cam,
    camGone,
    camHelp,
    camLiveUi,
    camState,
    canConfirm,
    cancelTyping,
    checking,
    choose,
    closeBulk,
    confirmCapture,
    deciding,
    deskEnd,
    discard,
    discardAsk,
    discardGo,
    doneSnap,
    dropTraining,
    dup,
    dupGoRef,
    fileShotsRef,
    filedToast,
    finishEnd,
    finishing,
    flash,
    flipCamera,
    flipTypeMode,
    flipping,
    footRef,
    hideAllowTip,
    iconBtn,
    ident,
    inspecting,
    isBulk,
    isNewItem,
    isPreset,
    leaveQuick,
    leaveScan,
    live,
    noBarcode,
    offlineBulk,
    onFileCand,
    openTyping,
    phase,
    photoInRef,
    photoSub,
    photoTiles,
    photoTitle,
    photoUi,
    preScan,
    presetStuck,
    presetTitle,
    queue,
    queueRef,
    quickMode,
    readPhoto,
    reading,
    removeSaved,
    removeShot,
    resetToScanning,
    resume,
    resumeCamera,
    reticleRef,
    saveError,
    savedUi,
    saving,
    scanUi,
    setAwaiting,
    setBlConfirm,
    setBlView,
    setDeciding,
    setDiscardAsk,
    setDup,
    setFinishing,
    setIdentity,
    setInspecting,
    setPage,
    setResume,
    setShots,
    setTyped,
    sheetChose,
    shots,
    shotsRef,
    snap,
    snapMirror,
    snapMode,
    snapRef,
    stageRef,
    startCamera,
    submitTyped,
    takeMore,
    takeShot,
    tapCamera,
    tilesRef,
    toggleTorch,
    topRef,
    torchOn,
    typeAbc,
    typeInRef,
    typed,
    typing,
    videoRef,
  };
}
